import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { getStatusConfig } from '../../config/colorConfig';

const ExpiredContractsPopup = ({ isOpen, contracts = [], onDismiss }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const statusConfig = getStatusConfig(theme);
  const [isDismissing, setIsDismissing] = useState(false);

  const handleViewContract = (contractId) => {
    navigate(`/contracts/${contractId}`);
    onDismiss();
  };

  const handleDismiss = () => {
    setIsDismissing(true);
    setTimeout(() => {
      setIsDismissing(false);
      onDismiss();
    }, 300);
  };

  // Group contracts by urgency
  // critical: already expired (daysLeft < 0) or expires today (daysLeft === 0)
  const critical = contracts.filter(c => c.daysLeft <= 0);
  // urgent: expiring within 30 days
  const urgent = contracts.filter(c => c.daysLeft > 0 && c.daysLeft <= 30);
  // soon: expiring 31-60 days from now
  const soon = contracts.filter(c => c.daysLeft > 30 && c.daysLeft <= 60);

  const getUrgencyColor = (daysLeft) => {
    // Red for expired or expires today
    if (daysLeft <= 0) return { bg: '#a4262c', light: '#a4262c22', text: '#ff6b6b', badge: '🚨' };
    // Orange for expiring within 30 days
    if (daysLeft <= 30) return { bg: '#ca5010', light: '#ca501022', text: '#ff922b', badge: '🔴' };
    // Gold for 31-60 days
    return { bg: '#ffc107', light: '#ffc10722', text: '#ffb300', badge: '🟡' };
  };

  const getUrgencyLabel = (daysLeft) => {
    if (daysLeft < -1) return `${Math.abs(daysLeft)} days OVERDUE`;
    if (daysLeft === -1) return '1 day OVERDUE';
    if (daysLeft === 0) return 'EXPIRES TODAY';
    if (daysLeft === 1) return 'Expires tomorrow';
    return `${daysLeft} days remaining`;
  };

  const ContractCard = ({ contract }) => {
    const urgency = getUrgencyColor(contract.daysLeft);
    return (
      <div
        style={{
          background: theme.bg.secondary,
          border: `1px solid ${urgency.bg}33`,
          borderLeft: `4px solid ${urgency.bg}`,
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '12px',
          transition: 'all 0.2s',
          cursor: 'pointer',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = theme.bg.dark;
          e.currentTarget.style.borderColor = `${urgency.bg}66`;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = theme.bg.secondary;
          e.currentTarget.style.borderColor = `${urgency.bg}33`;
        }}
      >
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <div style={{ fontSize: '24px', minWidth: '30px' }}>{urgency.badge}</div>

          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Contract Ref */}
            <div
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: urgency.text,
                fontFamily: 'Courier New, monospace',
                marginBottom: '4px',
              }}
            >
              {contract.RefNo}
            </div>

            {/* Title */}
            <div
              style={{
                fontSize: '15px',
                fontWeight: '500',
                color: theme.text.primary,
                marginBottom: '8px',
                lineHeight: '1.3',
              }}
            >
              {contract.Title}
            </div>

            {/* Metadata */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '10px' }}>
              <div style={{ fontSize: '13px', color: theme.text.secondary }}>
                <span style={{ color: theme.text.tertiary }}>Vendor:</span> {contract.VendorName}
              </div>
              <div style={{ fontSize: '13px', color: theme.text.secondary }}>
                <span style={{ color: theme.text.tertiary }}>Expires:</span>{' '}
                {new Date(contract.ExpiryDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </div>
            </div>

            {/* Urgency Status */}
            <div
              style={{
                display: 'inline-block',
                padding: '4px 10px',
                background: urgency.light,
                border: `1px solid ${urgency.bg}66`,
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                color: urgency.text,
              }}
            >
              {getUrgencyLabel(contract.daysLeft)}
            </div>
          </div>

          {/* Status Badge */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              minWidth: '70px',
            }}
          >
            <div
              style={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: urgency.text,
                textAlign: 'center',
              }}
            >
              {contract.Status}
            </div>
          </div>
        </div>

        {/* Action Button */}
        <button
          onClick={() => handleViewContract(contract.id)}
          style={{
            marginTop: '12px',
            padding: '8px 14px',
            background: statusConfig.executed.bg,
            border: 'none',
            borderRadius: '6px',
            color: '#fff',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
            transition: 'all 0.2s',
          }}
          onMouseEnter={e => (e.target.style.opacity = '0.9')}
          onMouseLeave={e => (e.target.style.opacity = '1')}
        >
          View Contract →
        </button>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(4px)',
        padding: '20px',
      }}
      onClick={handleDismiss}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: theme.bg.secondary,
          border: '1px solid '+theme.border,
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.9)',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px',
            borderBottom: '1px solid '+theme.border,
            background: `linear-gradient(135deg, ${theme.bg.tertiary} 0%, ${theme.bg.secondary} 100%)`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <span style={{ fontSize: '28px' }}>⚠️</span>
            <h2
              style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: '28px',
                fontWeight: '600',
                color: theme.text.primary,
                margin: 0,
              }}
            >
              Renewable Contracts Requiring Action
            </h2>
          </div>
          <p style={{ color: theme.text.secondary, fontSize: '14px', margin: '0', marginTop: '4px' }}>
            {contracts.length} contract{contracts.length !== 1 ? 's' : ''} need your attention
          </p>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px',
          }}
        >
          {/* Summary Stats */}
          {(critical.length > 0 || urgent.length > 0 || soon.length > 0) && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '12px',
                marginBottom: '24px',
              }}
            >
              {critical.length > 0 && (
                <div
                  style={{
                    background: '#a4262c22',
                    border: '1px solid #a4262c44',
                    borderRadius: '8px',
                    padding: '12px',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: '24px', marginBottom: '4px' }}>🚨</div>
                  <div
                    style={{
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: '#ff6b6b',
                      marginBottom: '2px',
                    }}
                  >
                    {critical.length}
                  </div>
                  <div style={{ fontSize: '12px', color: theme.text.secondary }}>Expired/Urgent</div>
                </div>
              )}

              {urgent.length > 0 && (
                <div
                  style={{
                    background: '#ca501022',
                    border: '1px solid #ca501044',
                    borderRadius: '8px',
                    padding: '12px',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: '24px', marginBottom: '4px' }}>🔴</div>
                  <div
                    style={{
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: '#ff922b',
                      marginBottom: '2px',
                    }}
                  >
                    {urgent.length}
                  </div>
                  <div style={{ fontSize: '12px', color: theme.text.secondary }}>Within 30 Days</div>
                </div>
              )}

              {soon.length > 0 && (
                <div
                  style={{
                    background: '#ffc10722',
                    border: '1px solid #ffc10744',
                    borderRadius: '8px',
                    padding: '12px',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: '24px', marginBottom: '4px' }}>🟡</div>
                  <div
                    style={{
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: '#ffb300',
                      marginBottom: '2px',
                    }}
                  >
                    {soon.length}
                  </div>
                  <div style={{ fontSize: '12px', color: theme.text.secondary }}>31-60 Days</div>
                </div>
              )}
            </div>
          )}

          {/* Critical Contracts - Expired or Expires Today */}
          {critical.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#ff6b6b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span>🚨</span> Expired / Expires Today
              </div>
              {critical.map(c => (
                <ContractCard key={c.id} contract={c} />
              ))}
            </div>
          )}

          {/* Urgent Contracts - Expiring Soon */}
          {urgent.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#ff922b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span>🔴</span> Expiring Soon (1-30 Days)
              </div>
              {urgent.map(c => (
                <ContractCard key={c.id} contract={c} />
              ))}
            </div>
          )}

          {/* Coming Up - 31-60 Days */}
          {soon.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#ffb300',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span>🟡</span> Coming Up (31-60 Days)
              </div>
              {soon.map(c => (
                <ContractCard key={c.id} contract={c} />
              ))}
            </div>
          )}

          {contracts.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: theme.text.secondary }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>✨</div>
              <p>No contracts requiring action at this time</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid '+theme.border,
            background: theme.bg.tertiary,
            display: 'flex',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <button
            onClick={() => navigate('/admin/expiry')}
            style={{
              padding: '10px 16px',
              background: 'transparent',
              border: '1px solid '+theme.text.secondary,
              borderRadius: '6px',
              color: theme.text.secondary,
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => {
              e.target.style.background = theme.border;
              e.target.style.color = theme.accent;
            }}
            onMouseLeave={e => {
              e.target.style.background = 'transparent';
              e.target.style.color = theme.text.secondary;
            }}
          >
            View All Contracts
          </button>

          <button
            onClick={handleDismiss}
            disabled={isDismissing}
            style={{
              padding: '10px 20px',
              background: theme.accent,
              border: 'none',
              borderRadius: '6px',
              color: theme.bg.tertiary,
              fontSize: '14px',
              fontWeight: '600',
              cursor: isDismissing ? 'not-allowed' : 'pointer',
              fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
              opacity: isDismissing ? 0.7 : 1,
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => !isDismissing && (e.target.style.background = theme.accent+'dd')}
            onMouseLeave={e => !isDismissing && (e.target.style.background = theme.accent)}
          >
            {isDismissing ? 'Dismissing...' : 'Got it, thanks'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpiredContractsPopup;
